import { useState } from 'react';
import { Users } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { StatusBadge } from '@/components/ui/status-badge';
import { formatCurrency } from '@/utils/formatters';
import { FindNewPartnerDialog } from './FindNewPartnerDialog';

interface PartnerInfoDisplayProps {
  registration: {
    /** The registration row's UUID — used to drive the "Find Partner" reinvite. */
    id?: string;
    registration_type?: string;
    partner_name?: string;
    partner_first_name?: string;
    partner_last_name?: string;
    partner_status?: string;
    partner_email?: string;
    position?: string;
    /** Set on team_primary rows when the originating user paid for both seats. */
    paid_for_partner?: boolean | null;
    /** The PARTNER side's per-side cost (entry + txn + their non-member fee). */
    partner_payment_amount?: number | null;
    /** Display name of whoever captured the linked payment, when it differs from the row owner. */
    payment_payer_display_name?: string | null;
    [key: string]: unknown;
  };
  /**
   * Event UUID + display name + entry-close gate, threaded through so the
   * inline "Find Partner" CTA on a `needs_partner` row can open the same
   * FindNewPartnerDialog the dashboard uses without bouncing the user
   * back to the dashboard. All three are optional — the dialog will
   * still function with just registrationId, but the cost-reconciliation
   * preview needs eventId, and the close-time gate suppresses the CTA
   * past the cutoff (matching the dashboard behavior).
   */
  eventId?: string | null;
  eventName?: string;
  entriesHaveClosed?: boolean;
}

export function PartnerInfoDisplay({
  registration,
  eventId,
  eventName,
  entriesHaveClosed,
}: PartnerInfoDisplayProps) {
  const regType = registration.registration_type;
  if (regType === 'individual') return null;

  const partnerName =
    registration.partner_name ||
    (registration.partner_first_name && registration.partner_last_name
      ? `${registration.partner_first_name} ${registration.partner_last_name}`
      : null);

  const partnerStatus = registration.partner_status as string | undefined;
  const needsPartner = partnerStatus === 'needs_partner';

  // The reinvite endpoint targets the team_primary row (the user who
  // initiated the team), so the inline CTA is only meaningful when the
  // viewer is on their own team_primary registration. Past the entries-
  // close cutoff the backend rejects reinvite with 409, so we hide the
  // affordance to avoid teasing an unusable button (mirrors the
  // dashboard's gate). registrationId comes from the row's `id`.
  const registrationId =
    typeof registration.id === 'string' ? registration.id : null;
  const canFindPartner =
    needsPartner &&
    regType === 'team_primary' &&
    !entriesHaveClosed &&
    !!registrationId;
  const [findPartnerOpen, setFindPartnerOpen] = useState(false);

  // When this row is the team_primary AND the originating user paid for
  // the partner's seat, surface the partner side's per-side amount on
  // this card so the user can see the full picture without having to
  // hunt through the funds tab. Without this the badges row only shows
  // their OWN seat ("Paid $135") and gives no hint that the partner
  // side was also covered. partner_payment_amount is enriched server-
  // side in RegistrationService.getMyRegistrations.
  const paidForPartner =
    regType === 'team_primary' && registration.paid_for_partner === true;
  const partnerSidePaid = paidForPartner
    ? Number(registration.partner_payment_amount ?? NaN)
    : NaN;
  const showPartnerPaidLine =
    paidForPartner && Number.isFinite(partnerSidePaid) && partnerSidePaid > 0;

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-2">
          <Users className="h-4 w-4 text-muted-foreground" />
          <h4 className="text-sm font-medium">
            {regType === 'team_primary' ? 'Your Partner' : 'Picked by'}
          </h4>
        </div>

        {needsPartner ? (
          // The previous partner declined and the entry was flagged
          // pending-partner. Use the same red copy the dashboard uses
          // so the user sees a consistent "this entry is invalid until
          // re-paired" message wherever they look. When the viewer is
          // the team_primary, surface the "Find Partner" CTA inline so
          // they don't have to bounce back to the dashboard — opens the
          // same FindNewPartnerDialog the dashboard wires up.
          <div className="space-y-2">
            <p className="text-sm text-destructive">
              No partner — your previous partner declined. Find a new
              partner before entries close or this entry will be
              cancelled at the draw.
            </p>
            {canFindPartner && (
              <div className="flex justify-end">
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => setFindPartnerOpen(true)}
                >
                  Find Partner
                </Button>
              </div>
            )}
          </div>
        ) : partnerName ? (
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <div>
                <p className="text-sm font-medium">{partnerName}</p>
                {registration.partner_email && (
                  <p className="text-xs text-muted-foreground">
                    {registration.partner_email}
                  </p>
                )}
              </div>
              {partnerStatus && partnerStatus !== 'none' && (
                <StatusBadge status={partnerStatus} variant="registration" />
              )}
            </div>

            {showPartnerPaidLine && (
              <div className="flex items-center justify-between gap-2 rounded-md bg-emerald-50 dark:bg-emerald-950/30 px-2.5 py-1.5 text-xs text-emerald-800 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-900/40">
                <span>You paid for {partnerName}'s seat</span>
                <span className="font-semibold">
                  {formatCurrency(partnerSidePaid)}
                </span>
              </div>
            )}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            {regType === 'team_primary'
              ? 'Waiting for partner confirmation'
              : 'Partner info not available'}
          </p>
        )}
      </CardContent>
      {canFindPartner && registrationId && (
        <FindNewPartnerDialog
          open={findPartnerOpen}
          onClose={() => setFindPartnerOpen(false)}
          registrationId={registrationId}
          eventId={eventId ?? null}
          primaryPosition={(registration.position as string) ?? null}
          eventName={eventName}
        />
      )}
    </Card>
  );
}
